import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Check, Loader2, Plus, RefreshCw, Search, TrendingDown, TrendingUp, X } from 'lucide-react';
import {
  Badge,
  Button,
  DateRangeFilter,
  EmptyState,
  FilterPill,
  PageHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';
import { ordersService } from '../../services/orders.service';
import PlaceOrderModal from './PlaceOrderModal';
import {
  REJECT_REASON_EMPTY_LABEL,
  SIDE_OPTIONS,
  STATUS_OPTIONS,
  averagePrice,
  avatarToneClass,
  customerDisplayName,
  customerInitials,
  customerSubtitle,
  filledQuantity,
  fillRatio,
  fmtDateParts,
  fmtMoney,
  fmtQty,
  hasRejectReasonText,
  instrumentMarket,
  instrumentName,
  limitQuoteLabel,
  orderCurrency,
  orderCustomer,
  orderExpiration,
  orderMatchesQuery,
  orderShowsRejectReason,
  orderTypeLabel,
  statusConfig,
} from './orderPresentation';

const PAGE_SIZE = 50;

const QUICK_STATUS = [
  { label: 'All', value: '' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Partial', value: 'PARTIALLY_FILLED' },
  { label: 'Filled', value: 'FILLED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const DATE_LABELS = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

const SimpleDropdown = ({ value, options, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`btn btn-sm btn-secondary gap-1.5 ${value ? 'border-accent-300 text-accent-700 bg-accent-50' : ''}`}
      >
        <span>{current?.value ? current.label : placeholder}</span>
        <ChevronDown className="h-3.5 w-3.5 text-ink-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 w-48 bg-white rounded-xl shadow-elevated border border-ink-200 py-1 z-20 max-h-72 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value || 'all'}
              type="button"
              className={`w-full px-3 py-1.5 text-sm text-left flex items-center justify-between hover:bg-ink-50 ${
                value === opt.value ? 'text-accent-700 font-medium bg-accent-50' : 'text-ink-700'
              }`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
              {value === opt.value && <Check className="h-3.5 w-3.5 text-accent-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FillBar = ({ ratio }) => {
  if (ratio == null) return null;
  const pct = Math.round(ratio * 100);
  return (
    <div className="mt-1 ml-auto h-1.5 w-16 rounded-full bg-ink-100 overflow-hidden" aria-hidden="true">
      <div
        className={`h-full rounded-full ${ratio >= 1 ? 'bg-success-500' : 'bg-accent-500'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const RowSkeleton = () => (
  <TR>
    {Array.from({ length: 10 }).map((_, index) => (
      <TD key={index}>
        <div className="h-3 w-16 bg-ink-100 animate-pulse rounded" />
      </TD>
    ))}
  </TR>
);

const OrdersList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [query, setQuery] = useState('');
  const [sideFilter, setSideFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerIdFilter, setCustomerIdFilter] = useState(searchParams.get('customerId') || '');
  const [dateRange, setDateRange] = useState('all');
  const [showPlaceOrder, setShowPlaceOrder] = useState(false);

  const fetchOrders = useCallback(async (params = {}) => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await ordersService.listOrders({
        limit: PAGE_SIZE,
        side: params.side || undefined,
        customerId: params.customerId || undefined,
      });
      const list = Array.isArray(result?.orders) ? result.orders : [];
      setOrders(list);
      setNextCursor(result?.nextCursor || null);
      setTotal(typeof result?.total === 'number' ? result.total : null);
    } catch {
      setLoadError(true);
      setOrders([]);
      setTotal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders({ side: sideFilter, customerId: customerIdFilter });
  }, [sideFilter, customerIdFilter, fetchOrders]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await ordersService.listOrders({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        side: sideFilter || undefined,
        customerId: customerIdFilter || undefined,
      });
      const list = Array.isArray(result?.orders) ? result.orders : [];
      setOrders((prev) => [...prev, ...list]);
      setNextCursor(result?.nextCursor || null);
      if (typeof result?.total === 'number') setTotal(result.total);
    } catch {
      // Keep the rows already on screen if the next page fails.
    } finally {
      setLoadingMore(false);
    }
  };

  const inDateRange = (dateStr, range) => {
    if (!range || range === 'all') return true;
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = { today: 1, '7d': 7, '30d': 30, '90d': 90 };
    return diff < (days[range] || Infinity) * 86400000;
  };

  const searched = orders.filter((order) => (
    inDateRange(order.createdAt, dateRange)
    && orderMatchesQuery(order, query)
  ));
  const statusCounts = searched.reduce((counts, order) => {
    if (order.status) counts[order.status] = (counts[order.status] || 0) + 1;
    return counts;
  }, {});
  const filtered = statusFilter
    ? searched.filter((order) => order.status === statusFilter)
    : searched;

  const activeFilters = [];
  if (customerIdFilter) activeFilters.push({ key: 'customer', label: 'Customer', value: customerIdFilter, clear: () => setCustomerIdFilter('') });
  if (query.trim()) activeFilters.push({ key: 'query', label: 'Search', value: query.trim(), clear: () => setQuery('') });
  if (sideFilter) activeFilters.push({ key: 'side', label: 'Side', value: sideFilter, clear: () => setSideFilter('') });
  if (statusFilter) {
    const label = STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter;
    activeFilters.push({ key: 'status', label: 'Status', value: label, clear: () => setStatusFilter('') });
  }
  if (dateRange !== 'all') {
    activeFilters.push({ key: 'date', label: 'Date', value: DATE_LABELS[dateRange] || dateRange, clear: () => setDateRange('all') });
  }

  const clearAll = () => {
    setCustomerIdFilter('');
    setQuery('');
    setSideFilter('');
    setStatusFilter('');
    setDateRange('all');
  };

  const refresh = () => {
    fetchOrders({ side: sideFilter, customerId: customerIdFilter });
  };

  const openOrder = (orderId) => {
    if (!orderId) return;
    navigate(`/orders/${orderId}`);
  };

  const clientFiltered = Boolean(query.trim()) || dateRange !== 'all' || Boolean(statusFilter);
  const countLabel = total != null && !clientFiltered
    ? `${filtered.length.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`
    : `${filtered.length.toLocaleString('en-US')} shown`;

  return (
    <div>
      <PageHeader
        title="Orders"
        description="Blotter for customer orders. Select a row to open the full order dossier."
        actions={(
          <div className="flex items-center gap-3">
            {typeof total === 'number' && (
              <span className="text-sm text-ink-500 tnum">{total.toLocaleString('en-US')} total</span>
            )}
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="btn btn-sm btn-secondary"
              aria-label="Refresh orders"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Button variant="primary" iconLeft={Plus} onClick={() => setShowPlaceOrder(true)}>
              Place order
            </Button>
          </div>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search symbol, order, or customer…"
            className="input pl-8 text-sm h-9"
          />
        </div>
        <div className="relative min-w-[180px]">
          <input
            type="text"
            value={customerIdFilter}
            onChange={(e) => setCustomerIdFilter(e.target.value)}
            placeholder="Filter by customer ID…"
            className="input text-sm h-9"
          />
          {customerIdFilter && (
            <button
              type="button"
              onClick={() => setCustomerIdFilter('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
              aria-label="Clear customer filter"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <SimpleDropdown value={sideFilter} options={SIDE_OPTIONS} onChange={setSideFilter} placeholder="Side" />
        <SimpleDropdown value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} placeholder="Status" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        {activeFilters.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-ink-500 hover:text-ink-800 flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {QUICK_STATUS.map((item) => {
          const active = statusFilter === item.value;
          const count = item.value ? (statusCounts[item.value] || 0) : searched.length;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                if (!item.value) {
                  setStatusFilter('');
                  return;
                }
                setStatusFilter((current) => (current === item.value ? '' : item.value));
              }}
              className={`badge cursor-pointer text-xs gap-1.5 ${
                active
                  ? 'bg-accent-50 text-accent-700 border-accent-200'
                  : 'bg-white text-ink-500 border-ink-200 hover:bg-ink-50'
              }`}
            >
              {`${item.label} ${count}`}
            </button>
          );
        })}
      </div>

      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {activeFilters.map((f) => (
            <FilterPill key={f.key} label={f.label} value={f.value} onRemove={f.clear} />
          ))}
        </div>
      )}

      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-100">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-semibold text-ink-900">Orders</h2>
            <span className="text-xs text-ink-500 tnum">{loading ? 'Loading…' : countLabel}</span>
          </div>
          {!loading && nextCursor && (
            <span className="text-2xs text-ink-400">More orders are available</span>
          )}
        </div>
        {loadError ? (
          <EmptyState
            icon={Search}
            title="Failed to load orders"
            description="There was a problem fetching orders. Check your connection and try again."
            action={(
              <button
                type="button"
                onClick={() => fetchOrders({ side: sideFilter, customerId: customerIdFilter })}
                className="text-sm font-medium text-accent-700"
              >
                Retry
              </button>
            )}
          />
        ) : !loading && filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={activeFilters.length > 0 ? 'No orders match your filters' : 'No orders yet'}
            description={
              activeFilters.length > 0
                ? 'Try a different symbol, customer, or status.'
                : 'Orders placed for customers in this organization will appear here.'
            }
            action={
              activeFilters.length > 0 && (
                <button type="button" onClick={clearAll} className="text-sm font-medium text-accent-700">
                  Clear filters
                </button>
              )
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table table-dense">
              <THead>
                <TR>
                  <TH>Created</TH>
                  <TH>Customer</TH>
                  <TH>Instrument</TH>
                  <TH>Side</TH>
                  <TH>Type</TH>
                  <TH className="text-right">Qty / Filled</TH>
                  <TH className="text-right">Avg price</TH>
                  <TH className="text-right">Limit</TH>
                  <TH>Status</TH>
                  <TH className="w-8 sticky right-0 bg-ink-50" />
                </TR>
              </THead>
              <TBody>
                {loading
                  ? Array.from({ length: 8 }).map((_, index) => <RowSkeleton key={index} />)
                  : filtered.map((order) => {
                    const statusCfg = statusConfig(order.status);
                    const customer = orderCustomer(order);
                    const name = customerDisplayName(customer);
                    const subtitle = customerSubtitle(order);
                    const instrument = instrumentName(order);
                    const market = instrumentMarket(order);
                    const filled = filledQuantity(order);
                    const avg = averagePrice(order);
                    const currency = orderCurrency(order);
                    const ratio = fillRatio(order);
                    const created = fmtDateParts(order.createdAt);
                    const expiration = orderExpiration(order);
                    return (
                      <TR
                        key={order.id}
                        className="cursor-pointer group"
                        onClick={() => openOrder(order.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openOrder(order.id);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open order ${order.symbol || order.id || ''}`.trim()}
                      >
                        <TD className="align-middle whitespace-nowrap">
                          <p className="text-ink-800 tnum leading-tight">{created?.date || '—'}</p>
                          <p className="text-2xs text-ink-400 tnum leading-tight font-mono truncate max-w-[10rem]" title={order.id || undefined}>
                            {[created?.time, order.id].filter(Boolean).join(' · ') || '—'}
                          </p>
                        </TD>
                        <TD className="align-middle">
                          <div className="flex items-center gap-2 min-w-[11rem] max-w-[16rem]">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarToneClass(order.customerId)}`}>
                              {customerInitials(customer, order.customerId)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-ink-900 truncate">
                                {name || order.customerId || '—'}
                              </p>
                              {subtitle && (
                                <p className="text-2xs text-ink-500 truncate">{subtitle}</p>
                              )}
                            </div>
                          </div>
                        </TD>
                        <TD className="align-middle">
                          <div className="min-w-[7rem] max-w-[14rem]">
                            <div className="flex items-center gap-1.5">
                              {order.side === 'BUY' && <TrendingUp className="h-3.5 w-3.5 text-success-600 shrink-0" aria-hidden="true" />}
                              {order.side === 'SELL' && <TrendingDown className="h-3.5 w-3.5 text-danger-600 shrink-0" aria-hidden="true" />}
                              <p className="font-semibold text-ink-900">{order.symbol || '—'}</p>
                              {market && (
                                <span className="text-2xs font-medium text-ink-500 bg-ink-100 rounded px-1">{market}</span>
                              )}
                            </div>
                            <p className="text-2xs text-ink-500 truncate">{instrument || '—'}</p>
                          </div>
                        </TD>
                        <TD className="align-middle">
                          <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold border ${
                            order.side === 'BUY'
                              ? 'bg-success-50 text-success-700 border-success-100'
                              : order.side === 'SELL'
                                ? 'bg-danger-50 text-danger-700 border-danger-100'
                                : 'bg-ink-50 text-ink-600 border-ink-200'
                          }`}>
                            {order.side || '—'}
                          </span>
                        </TD>
                        <TD className="align-middle">
                          <p className="text-ink-800">{orderTypeLabel(order.type) || '—'}</p>
                          {expiration && <p className="text-2xs text-ink-400">{expiration}</p>}
                        </TD>
                        <TD className="align-middle text-right">
                          <p className="font-mono tnum text-ink-900">
                            <span>{filled == null ? '—' : fmtQty(filled)}</span>
                            <span className="text-ink-300"> / </span>
                            <span>{fmtQty(order.quantity)}</span>
                          </p>
                          <FillBar ratio={ratio} />
                        </TD>
                        <TD className="align-middle text-right font-mono tnum">{avg == null ? '—' : fmtMoney(avg, currency)}</TD>
                        <TD className="align-middle text-right font-mono tnum">{limitQuoteLabel(order)}</TD>
                        <TD className="align-middle">
                          <Badge tone={statusCfg.tone} dot>{statusCfg.label}</Badge>
                          {orderShowsRejectReason(order.status) && (
                            <p
                              className={`mt-1 max-w-[12rem] line-clamp-2 rounded px-1.5 py-0.5 text-2xs leading-snug ${
                                hasRejectReasonText(order.rejectReason)
                                  ? order.status === 'FAILED'
                                    ? 'bg-warning-50 text-warning-800'
                                    : 'bg-danger-50 text-danger-800'
                                  : 'text-ink-400'
                              }`}
                              title={hasRejectReasonText(order.rejectReason) ? order.rejectReason : REJECT_REASON_EMPTY_LABEL}
                            >
                              {hasRejectReasonText(order.rejectReason) ? order.rejectReason : REJECT_REASON_EMPTY_LABEL}
                            </p>
                          )}
                        </TD>
                        <TD className="align-middle sticky right-0 bg-white group-hover:bg-ink-50">
                          <span className="inline-flex items-center gap-0.5 text-ink-300 group-hover:text-accent-700">
                            <span className="text-2xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Open</span>
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </TD>
                      </TR>
                    );
                  })}
              </TBody>
            </table>
          </div>
        )}

        {!loading && !loadError && filtered.length > 0 && nextCursor && (
          <div className="flex justify-center py-3 border-t border-ink-100">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="btn btn-sm btn-secondary"
            >
              {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Load more
            </button>
          </div>
        )}
      </div>

      {showPlaceOrder && (
        <PlaceOrderModal
          onClose={() => setShowPlaceOrder(false)}
          onSuccess={() => fetchOrders({ side: sideFilter, customerId: customerIdFilter })}
          initialCustomerId={customerIdFilter}
        />
      )}
    </div>
  );
};

export default OrdersList;
